import type { BuyerSequenceFieldInfoDto } from "@/api/buyerSequence";
import { StatusBadge } from "@/design-system/components/ui";
import { cn } from "@/design-system/lib/utils";
import { CONDITION_END_LABEL, conditionLabel, tokenizeTemplate } from "./templateTokens";

/**
 * Wording as it reads, with every merge field drawn as a chip carrying the
 * field's label and every condition as a chip of its own ("if listing", "if no
 * listing", "end"). The raw `{{…}}` syntax stays in the textarea beside it; this
 * is what the syntax means.
 *
 * A name the field list does not know is drawn as a warning chip, not dropped:
 * the server will refuse the save, and the chip is where the person sees why.
 */
export function TemplateChips({
  text,
  fields,
  className,
  "data-testid": testId,
}: {
  text: string;
  fields: readonly BuyerSequenceFieldInfoDto[] | undefined;
  className?: string;
  "data-testid"?: string;
}) {
  const byName = new Map((fields ?? []).map((f) => [f.name, f]));
  const tokens = tokenizeTemplate(text);

  return (
    <div
      className={cn(
        "whitespace-pre-wrap break-words text-small leading-relaxed text-onyx",
        className,
      )}
      data-testid={testId}
    >
      {tokens.length === 0 ? (
        <span className="text-gray-mid">Empty.</span>
      ) : (
        tokens.map((t, i) => {
          switch (t.kind) {
            case "text":
              return <span key={i}>{t.text}</span>;
            case "field": {
              const field = byName.get(t.name);
              // Until the field list arrives there is nothing to judge a name
              // against, so it is drawn as a field under its own name rather
              // than flagged — a warning that vanishes a moment later is noise.
              if (!fields) {
                return (
                  <StatusBadge
                    key={i}
                    tone="progress"
                    className="mx-[1px]"
                    data-token="field"
                    data-field={t.name}
                  >
                    {t.name}
                  </StatusBadge>
                );
              }
              if (!field || field.isFlag) {
                return (
                  <StatusBadge
                    key={i}
                    tone="danger"
                    className="mx-[1px]"
                    data-token="unknown"
                    data-field={t.name}
                  >
                    {t.raw} — not a field
                  </StatusBadge>
                );
              }
              return (
                <StatusBadge
                  key={i}
                  tone="progress"
                  className="mx-[1px]"
                  data-token="field"
                  data-field={t.name}
                >
                  {field.label}
                </StatusBadge>
              );
            }
            case "open":
              return (
                <StatusBadge
                  key={i}
                  tone="info"
                  className="mx-[1px]"
                  data-token="condition"
                  data-field={t.name}
                >
                  {conditionLabel(t.name, t.negated)}
                </StatusBadge>
              );
            case "close":
              return (
                <StatusBadge
                  key={i}
                  tone="info"
                  className="mx-[1px]"
                  data-token="end"
                  data-field={t.name}
                >
                  {CONDITION_END_LABEL}
                </StatusBadge>
              );
          }
        })
      )}
    </div>
  );
}
